import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { facebook as fbClient } from "api/facebook/facebook-client";
import { useIsMountedRef } from "hooks/useIsMountedRef";
import { usePlatformActions } from "hooks/usePlatformActions";
import { AppDispatch } from "store/store";
import { FacebookProfile } from "store/platforms/types";
import { getFacebookUserProfile } from "store/platforms/thunks";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { RootState } from "store/reducers";
import { PlatformId } from "views/page-content/platforms/types";
import rollbar from "helpers/rollbar";
import fbFetch from "api/facebook/facebook-helper";

export const useFacebookPlatformSettingsForm = () => {
    const dispatch = useDispatch<AppDispatch>();
    const isMounted = useIsMountedRef();
    const { unlink } = usePlatformActions(PlatformId.Facebook);
    const profileLoadedRef = useRef(false);

    const { profileInfo } = useSelector(
        (state: RootState) => state.platforms.facebook
    );

    const [loading, setLoading] = useState(true);
    const [fbLoadingMore, setFbLoadingMore] = useState(false);
    const [timelineDestinations, setTimelineDestinations] = useState<
        FacebookProfile[]
    >([]);
    const [pageDestinations, setPageDestinations] = useState<FacebookProfile[]>(
        []
    );
    const [isPaginated, setIsPaginated] = useState(false);
    const [nextPage, setNextPage] = useState(undefined);

    /**
     * Load the user profile on component mount
     */
    useEffect(() => {
        async function init() {
            setLoading(true);
            try {
                await dispatch(getFacebookUserProfile());
                profileLoadedRef.current = true;
            } catch (e) {
                rollbar.error("Error getting facebook user profile", e, {
                    platformId: PlatformId.Facebook
                });
            }
        }
        if (isMounted.current) init();
    }, [dispatch, isMounted, unlink, setLoading]);

    /**
     * Fetch Facebook destinations (pages and timeline)
     */
    useEffect(() => {
        async function fetchDestinations() {
            try {
                // Fetch page and timeline destinations from Facebook
                const pageResults =
                    await fbClient.helpers.getDestinationsByType({
                        edge: "accounts"
                    });
                const timelineResults =
                    await fbClient.helpers.getDestinationById("me");

                if (timelineResults) setTimelineDestinations([timelineResults]);
                if (pageResults) setPageDestinations(pageResults.data);

                // Handle Page destination pagination
                if (pageResults?.getNextPage) {
                    setIsPaginated(true);
                    setNextPage(pageResults?.getNextPage);
                } else {
                    setIsPaginated(false);
                }
            } catch (e) {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "errors:facebook-load-error"
                    })
                );
            }
            setLoading(false);
        }
        fetchDestinations();
    }, [dispatch, profileInfo, setLoading]);

    /**
     * Load more pages when pagination is used
     */
    const loadMore = useCallback(async () => {
        setFbLoadingMore(true);

        try {
            const res = await fbFetch(nextPage);
            setPageDestinations((prev) => [...prev, ...res.data]);
            if (res?.getNextPage) {
                setNextPage(res.getNextPage);
                return;
            }
            setIsPaginated(false);
        } catch (e) {
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: "errors:facebook-load-error"
                })
            );
        } finally {
            setFbLoadingMore(false);
        }
    }, [nextPage, setPageDestinations, setNextPage, setIsPaginated, dispatch]);

    return {
        loading,
        timelineDestinations,
        pageDestinations,
        isPaginated,
        loadMore,
        fbLoadingMore
    };
};
